import { useState } from "react"
import PropTypes from "prop-types"
import { Checkbox, RadioButton } from "components/ui"
import { SavedBadge } from "components/shared/saved_badge"
import DateTimePicker from "components/admin/events/DateTimePicker"
import { apiAsync } from "utils/api"
import moment from "moment-timezone"
import { colors } from "components/shared/admin_colors"
import useFeature from "hooks/use_feature"
import { HelperShelfMessage, HelperShelfTrigger } from "components/helper_shelf"

let saveTimeout = null

const RegistrationStatus = ({ event: eventFromProps, mobileBrowser, isActingStaff }) => {
  const [event, setEvent] = useState(eventFromProps)
  const [openAt, setOpenAt] = useState(event.openAt)
  const [closeAt, setCloseAt] = useState(event.closeAt ?? event.endTime)
  const [isScheduledToClose, setIsScheduledToClose] = useState(event.scheduledToClose)
  const [isScheduledToOpen, setIsScheduledToOpen] = useState(event.scheduledToOpen)
  const [canCancelRegistrations, setCanCancelRegistrations] = useState(
    event.allowRegistrantsToCancel
  )
  const [open, setOpen] = useState(event.open)
  const [closed, setClosed] = useState(event.closed)
  const [errors, setErrors] = useState({})
  const [isEventSaved, setIsEventSaved] = useState(false)
  const [editingOpenScheduled, setEditingOpenScheduled] = useState(false)
  const [editingClosedScheduled, setEditingClosedScheduled] = useState(false)

  const {
    available: unifiedSignupsAvailable,
    enabled: unifiedSignupsEnabled,
    toggle: toggleUnifiedSignups,
    isLoading: unifiedSignupsFeatureLoading
  } = useFeature("unified_detailed_signups_ccw", event.id)

  const updateEvent = async params => {
    setErrors([])
    setIsEventSaved(false)

    if (saveTimeout) {
      clearTimeout(saveTimeout)
    }

    const response = await apiAsync.put(event.paths.show, { event: params })

    if (response.errors) {
      setErrors(response.errors)
    } else {
      setEvent(response.event)
      setIsEventSaved(true)
      saveTimeout = setTimeout(() => setIsEventSaved(false), 5000)
    }
  }

  const handleStatusChange = ({ target: { value } }) => {
    setEditingOpenScheduled(true)

    if (value === "open") {
      const today = moment()
        .startOf("day")
        .format()

      setOpen(true)
      setClosed(false)
      setOpenAt(today)
      setIsScheduledToOpen(false)

      updateEvent({
        open_at: today,
        close_at: !event.scheduledToClose ? null : event.closeAt
      })
    }

    if (value === "closed") {
      setOpenAt("")
      setCloseAt("")
      setIsScheduledToOpen(false)
      setClosed(true)
      setOpen(false)
      updateEvent({
        open_at: null,
        close_at: null
      })
    }
  }

  const handleCloseAutomatically = ({ target: { checked } }) => {
    setIsScheduledToClose(checked)
    setEditingClosedScheduled(true)

    if (checked === false) {
      setCloseAt("")
      updateEvent({ close_at: null })
    } else {
      setCloseAt(event.closeAt ?? event.endTime)
    }
  }

  const handleOpenAutomatically = ({ target: { checked } }) => {
    if (checked) {
      setOpenAt(
        moment()
          .startOf("day")
          .add(1, "day")
          .format()
      )
    }

    setIsScheduledToOpen(checked)
    setEditingOpenScheduled(true)
  }

  const handleOpenAtSave = () => {
    if (!moment(openAt).isValid()) {
      setErrors({
        open_at: ["and Time cannot be empty"]
      })
      return
    }

    setEditingOpenScheduled(false)

    updateEvent({
      open_at: openAt
    })
  }

  const handleCloseAtSave = () => {
    if (!moment(closeAt).isValid()) {
      setErrors({
        close_at: ["and Time cannot be empty"]
      })
      return
    }

    setEditingClosedScheduled(false)

    updateEvent({
      close_at: closeAt
    })
  }

  const handleCancelRegistrations = ({ target: { checked } }) => {
    setCanCancelRegistrations(checked)

    updateEvent({ allow_registrants_to_cancel: checked })
  }

  return (
    <>
      {event.isArchived && (
        <div className="pane">
          <div className="pane__header d-f ai-c jc-sb">
            <h2 className="pane__title">Registration status</h2>
            {isEventSaved ? <SavedBadge /> : null}
          </div>
          <div className="p-2 c-neutral-600">
            This signup is archived so it can not be registered for on Church Center.
          </div>
        </div>
      )}
      {!event.isArchived && (
        <div className="pane">
          <div className="pane__header d-f ai-c jc-sb">
            <h2 className="pane__title">Registration status</h2>
            {isEventSaved ? <SavedBadge /> : null}
          </div>
          {!event.isSimple && !unifiedSignupsFeatureLoading && !unifiedSignupsEnabled && (
            <div className="m-2 mb-0 warning-alert alert">
              This signup is currently using the legacy workflow on Church Center. Later this year,
              the legacy workflow will be discontinued and this signup will be moved to the new
              workflow. If you want to move to the new workflow earlier, please contact us.
            </div>
          )}
          <div className="p-2">
            <RadioButton
              text="Open"
              name="status"
              htmlFor="statusOpen"
              checked={open && !isScheduledToOpen}
              value="open"
              onSelect={handleStatusChange}
            />
            <RadioButton
              text="Scheduled to open"
              name="status"
              htmlFor="statusScheduled"
              checked={isScheduledToOpen}
              value="scheduled"
              onSelect={handleOpenAutomatically}
            />
            {errors.open_at && <div className="c-error pt-4p pl-3">Date {errors.open_at[0]}</div>}
            {isScheduledToOpen && (
              <div className="mb-1">
                {editingOpenScheduled ? (
                  <DateTimePicker
                    datetime={openAt}
                    onChange={setOpenAt}
                    onButtonClick={handleOpenAtSave}
                    isMobile={mobileBrowser}
                    errors={{
                      date: errors.open_at
                    }}
                  />
                ) : (
                  <>
                    {openAt && (
                      <div className="d-f ai-c pl-3" css={{ color: colors.neutral_020 }}>
                        on {moment(openAt).format(momentDateFormat)} at{" "}
                        {moment(openAt).format(momentTimeFormat)}
                        <button
                          className="xs-btn quiet-btn btn2 ml-1"
                          onClick={() => setEditingOpenScheduled(true)}
                        >
                          Edit
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
            <RadioButton
              text="Closed"
              name="status"
              htmlFor="statusClosed"
              checked={!isScheduledToOpen && closed}
              value="closed"
              wrapperClasses=""
              onSelect={handleStatusChange}
            />
          </div>
          {(open || isScheduledToOpen) && (
            <div className="px-2 pb-1">
              <Checkbox
                text="Close registration automatically"
                htmlFor="closeAutomatically"
                value="closeAutomatically"
                checked={isScheduledToClose}
                onSelect={handleCloseAutomatically}
              />
              {errors.close_at && (
                <div className="c-error pt-4p pl-3">Date {errors.close_at[0]}</div>
              )}
              {isScheduledToClose && (
                <>
                  {editingClosedScheduled ? (
                    <div className="pb-1">
                      <DateTimePicker
                        datetime={closeAt}
                        onChange={setCloseAt}
                        onButtonClick={handleCloseAtSave}
                        isMobile={mobileBrowser}
                        errors={{
                          date: errors.close_at
                        }}
                      />
                    </div>
                  ) : (
                    <>
                      {closeAt && (
                        <div className="d-f ai-c pl-3 mb-1" css={{ color: colors.neutral_020 }}>
                          on {moment(closeAt).format(momentDateFormat)} at{" "}
                          {moment(closeAt).format(momentTimeFormat)}
                          <button
                            className="xs-btn quiet-btn btn2 ml-1"
                            onClick={() => setEditingClosedScheduled(true)}
                          >
                            Edit
                          </button>
                        </div>
                      )}
                    </>
                  )}
                </>
              )}
            </div>
          )}

          <div className="p-2 line">
            <Checkbox
              text="Congregants can cancel their own registrations"
              htmlFor="allowCancel"
              value="allowCancel"
              checked={canCancelRegistrations}
              onSelect={handleCancelRegistrations}
            />
          </div>

          {isActingStaff && unifiedSignupsAvailable && !event.isSimple && (
            <div className="p-2 line staff-only">
              <Checkbox
                text={
                  <span>
                    Use the legacy workflow on Church Center for this signup{""}
                    <HelperShelfTrigger
                      id="unified_signups_toggle_helper"
                      className="p-r t-1p pl-1"
                    />{" "}
                  </span>
                }
                htmlFor="useLegacyFlow"
                value="useLegacyFlow"
                checked={!unifiedSignupsEnabled}
                onSelect={() => toggleUnifiedSignups("unified_detailed_signups_ccw")}
                data={{ "data-pendo": "triggerWorkflowPoll" }}
              />
              <HelperShelfMessage id="unified_signups_toggle_helper">
                When unchecked, this signup will use the new streamlined registration workflow on
                Church Center.{" "}
                <a
                  target="_blank"
                  rel="noreferrer"
                  href="https://pcoregistrations.zendesk.com/hc/en-us/articles/11788284033051"
                >
                  Learn more
                </a>
              </HelperShelfMessage>
            </div>
          )}
        </div>
      )}
    </>
  )
}

RegistrationStatus.propTypes = {
  mobileBrowser: PropTypes.bool,
  isActingStaff: PropTypes.bool,
  event: PropTypes.shape({
    id: PropTypes.number,
    scheduledToOpen: PropTypes.bool,
    scheduledToClose: PropTypes.bool,
    open: PropTypes.bool,
    openAt: PropTypes.string,
    closed: PropTypes.bool,
    closeAt: PropTypes.string,
    endTime: PropTypes.string,
    isSimple: PropTypes.bool,
    allowRegistrantsToCancel: PropTypes.bool,
    isArchived: PropTypes.bool,
    paths: PropTypes.shape({
      show: PropTypes.string
    })
  })
}

export default RegistrationStatus
